import { useEffect, useState } from "react";
import { format } from "date-fns";

import {
  useForm,
  FormProvider,
  Controller,
  useFieldArray,
} from "react-hook-form";
import { useAuth } from "react-oidc-context";
import { ThreeDots } from "react-loader-spinner";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Info from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";

import BackIcon from "@mui/icons-material/ArrowBackIosNew";

import { useNitroStore } from "../../store/Store";
import FormField from "../../components/FormField";
import IndicationDetails from "./components/IndicationDetails";
import CompanyFilter from "../../components/filters/CompanyFilter";
import IIndication from "../../types/IIndication";

import DeleteIndicationConfirmation from "./components/DeleteIndicationConfirmation";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import NitroService from "../../utils/nitro.service";
import { IndicationsSchema } from "./schemas/indicationSchema";
import NitroModal from "../../components/Modal";

import "./index.css";
import { useIsMobile } from "../../hooks/useIsMobile";
import IIndicationsRequest from "./types/IIndicationsRequest";
import UnsavedConfirmation from "../../components/unsavedConfirmation";
import cookie from "../../store/cookie";

import CurrencyFilter from "../../components/filters/currencyFilterButton";
import UOMFilter from "../../components/filters/UomFilter";
import useEscapeKeyExit from "../../hooks/useEscapeKeyExit";
import TeamsMessage from "./lib/teamsMessage";
import useValidationResolve from "../../hooks/useValidationResolve";
import { IPinExtension } from "../../types/ILocation";

const now = new Date();

const buildBlankIndication = (location?: IPinExtension): IIndication => {
  let origin = "";
  let competitor = "";

  if (location?.pinType === "competitor") {
    origin = location?.name || "";
    competitor = location?.owner || "";
  }

  return {
    type: "FOB",
    priceType: "single",
    product: "",
    mode: "",
    competitor,
    quantity: 0,
    startDate: format(
      new Date(now.getFullYear(), now.getMonth(), 1),
      "yyyy/MM/dd",
    ).replaceAll("/", "-"),
    endDate: format(
      new Date(now.getFullYear(), now.getMonth() + 1, 0),
      "yyyy/MM/dd",
    ).replaceAll("/", "-"),
    comments: "",
    prices: [{ origin, fobPrice: 0 }],
    postToTeams: true,
  };
};

const AddFOBIndicationPanel = () => {
  const auth = useAuth();
  const [
    showAddFOBIndicationPanel,
    setShowAddFOBIndicationPanel,
    savedNotification,
    failedNotification,
    loadLocations,
    selectedLocation,
    uom,
    setUom,
    currency,
    setCurrency,
    searchStarted,
  ] = useNitroStore((state) => [
    state.showAddFOBIndicationPanel,
    state.setShowAddFOBIndicationPanel,
    state.savedNotification,
    state.failedNotification,
    state.loadLocations,
    state.selectedLocation,
    state.uom,
    state.setUom,
    state.currency,
    state.setCurrency,
    state.searchStarted,
  ]);

  // TODO: mobile flag

  const form = useForm<IIndicationsRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: useValidationResolve(IndicationsSchema),
    defaultValues: {
      contactName: "",
      party:
        selectedLocation?.pinType === "customer"
          ? selectedLocation?.owner || ""
          : "",
      marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
      currency: cookie.currency,
      uom: cookie.uom,
      indications: [buildBlankIndication()],
      isKoch: false,
    },
  });
  const indications = useFieldArray({
    control: form.control,
    name: "indications",
    keyName: "key",
  });
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);

  useEffect(() => {
    form.clearErrors();
    form.reset({
      contactName: "",
      party:
        selectedLocation?.pinType === "customer"
          ? selectedLocation?.owner || ""
          : "",
      marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,

      currency: cookie.currency,
      uom: cookie.uom,
      indications: [buildBlankIndication(selectedLocation)],
      isKoch: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddFOBIndicationPanel]);

  const isMobile = useIsMobile(1024);

  // ################################################################################################################################
  // ## INDICATIONS #################################################################################################################
  // ################################################################################################################################
  const [total, setTotal] = useState(1);
  useEffect(() => {
    if (total !== indications.fields.length) {
      setTotal(indications.fields.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indications]);

  const nextIndication = () => {
    indications.append(buildBlankIndication(selectedLocation));
  };

  const copyIndication = (index: number) => {
    form.clearErrors();

    const indication = form.getValues().indications[index];
    if (!indication) return;

    indications.append({
      ...buildBlankIndication(selectedLocation),
      startDate: indication.startDate,
      endDate: indication.endDate,
      product: indication.product,
      mode: indication.mode,
    });
  };

  const deleteIndication = () => {
    if (deleteIndicationIndex === undefined) return;
    indications.remove(deleteIndicationIndex);
    setDeleteIndicationIndex(undefined);
  };
  // ################################################################################################################################
  // ## END INDICATIONS #############################################################################################################
  // ################################################################################################################################

  const [deleteIndicationIndex, setDeleteIndicationIndex] = useState<
    number | undefined
  >(undefined);
  const startDeleteIndication = (index: number) => {
    setDeleteIndicationIndex(index);
  };

  const submit = async (record: IIndicationsRequest) => {
    try {
      // ################################################################################################################################
      // ## POST TO TEAMS ###############################################################################################################
      // ################################################################################################################################

      if (
        !isMobile &&
        record.indications.filter((r) => r.postToTeams).length > 0
      ) {
        try {
          await TeamsMessage.postIndication(record);

          savedNotification("Indication posted to Teams.");
        } catch (err) {
          console.error("Failed to post indication to Teams", err);
          failedNotification(
            "Failed to post indication to Teams.",
            (err as any).message,
          );
          throw new Error("Failed to post indication to Teams.");
        }
      }

      // ################################################################################################################################
      // ## SAVE INDICATION #############################################################################################################
      // ################################################################################################################################
      try {
        const indicationRequest = {
          ...record,
          indications: record.indications.map((indication) => {
            return {
              ...indication,
              modes: [indication.mode],
              prices: indication.prices?.filter(
                (price: {
                  origin: string;
                  fobPrice?: number;
                  fobMinPrice?: number;
                  fobMaxPrice?: number;
                }) => {
                  if (!price.origin) {
                    return false;
                  }

                  if (indication.priceType === "single") {
                    if (!price.fobPrice) {
                      return false;
                    }
                  } else if (!price.fobMinPrice || !price.fobMaxPrice) {
                    return false;
                  }

                  return true;
                },
              ),
            };
          }),
        };

        console.log("Submit record", { indicationRequest });

        await NitroService.createIndications(indicationRequest as any);
        savedNotification("Successfully created indication.");
      } catch (err) {
        console.error("Failed to create indication", err);
        throw new Error("Failed to create indication.");
      }

      setShowAddFOBIndicationPanel(false);
      loadLocations({ panTo: true });
    } catch (err) {
      failedNotification("Failed to create indication.", (err as any).message);
    }
  };

  const contactName = form.watch("contactName");
  const party = form.watch("party");
  const [indicationsDisabled, setIndicationsDisabled] = useState(true);
  useEffect(() => {
    if (contactName && contactName.length > 3 && party) {
      setIndicationsDisabled(false);
    } else {
      setIndicationsDisabled(true);
    }
  }, [contactName, party]);

  const attemptClose = () => {
    if (Object.keys(form.formState.dirtyFields).length > 0) {
      setCloseConfirmationOpen(true);
    } else {
      setShowAddFOBIndicationPanel(false);
    }
  };

  useEscapeKeyExit({
    close: attemptClose,
    active: showAddFOBIndicationPanel,
  });

  return (
    <>
      {showAddFOBIndicationPanel && (
        <div
          id="hideMap"
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: isMobile ? 10003 : 1001,
            backgroundColor: "rgba(15, 44, 82, 0.3)", // Selecting pin location
            pointerEvents: isMobile ? "none" : "auto",
          }}
        />
      )}
      {isMobile && showAddFOBIndicationPanel ? (
        <div
          style={{
            zIndex: 10005,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <NitroModal
            id="AddFOBIndicationPanel"
            open={showAddFOBIndicationPanel}
            className={
              searchStarted
                ? "addFOBIndicationPanel"
                : "addFOBIndicationPanelLeft"
            }
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="FOBIndicationSearchBar"
              style={{ maxWidth: "100vw" }}
            >
              <IconButton
                style={{
                  borderRadius: 2,
                  height: "100%",
                  paddingLeft: 12,
                  paddingRight: 12,
                }}
                onClick={() => {
                  setShowAddFOBIndicationPanel(false);
                }}
              >
                <BackIcon />
              </IconButton>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  height: "100%",
                }}
              >
                <UOMFilter
                  value={uom}
                  setValue={(value) => setUom(value)}
                  isMobile
                />
                <CurrencyFilter
                  value={currency}
                  setValue={(value) => setCurrency(value)}
                  isMobile
                />
              </div>
            </div>
            <div className="FOBIndicationBody">
              {deleteIndicationIndex !== undefined && (
                <DeleteIndicationConfirmation
                  confirm={deleteIndication}
                  cancel={() => setDeleteIndicationIndex(undefined)}
                />
              )}
              {closeConfirmationOpen && (
                <UnsavedConfirmation
                  confirm={() => {
                    form.reset();
                    setShowAddFOBIndicationPanel(false);
                    setCloseConfirmationOpen(false);
                  }}
                  cancel={() => {
                    setCloseConfirmationOpen(false);
                  }}
                />
              )}

              {/* HEADER */}
              <div className="FOBIndicationHeader">
                <div
                  style={{
                    paddingLeft: 14,
                    fontWeight: 500,
                    fontSize: 26,
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  Market price indications
                </div>
              </div>
              {/* END HEADER */}

              {/* BODY */}
              <form
                onSubmit={form.handleSubmit(submit)}
                style={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflowX: "hidden",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                {form.formState.isSubmitting && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "rgba(255,255,255,.7)",
                    }}
                  >
                    <ThreeDots color="rgb(25, 25, 25)" />
                  </div>
                )}

                <div
                  style={{
                    padding: isMobile ? "0px 10px" : "0px 20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        paddingTop: 10,
                        color: "#148DD3",
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: "uppercase",
                      }}
                    >
                      Customer information
                    </div>
                  </div>

                  <div
                    style={{
                      paddingTop: 10,
                      paddingBottom: 20,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <FormProvider {...form}>
                      {/* FORM */}

                      <div
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          display: "flex",
                          gap: 20,
                        }}
                      >
                        <FormField
                          label="Contact"
                          errorMessage={
                            form.formState.errors.contactName?.message
                          }
                          width="calc((100% - 26px) / 2)"
                        >
                          <TextField
                            {...form.register("contactName")}
                            size="small"
                            fullWidth
                            placeholder="Contact name"
                          />
                        </FormField>
                        <Controller
                          name="party"
                          control={form.control}
                          render={({ field }) => (
                            <FormField
                              label="Company"
                              errorMessage={
                                form.formState.errors.party?.message
                              }
                              width="calc((100% - 20px) / 2)"
                            >
                              <CompanyFilter
                                value={field.value}
                                setValue={field.onChange}
                                isMobile={isMobile}
                              />
                            </FormField>
                          )}
                        />
                      </div>
                      {/* END FORM */}

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 20,
                        }}
                      >
                        {/* INDICATION DETAILS */}
                        {indications.fields.map((r, i) => {
                          return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={r.product}>
                              <IndicationDetails
                                index={i}
                                total={total}
                                nextIndication={nextIndication}
                                deleteIndication={() =>
                                  startDeleteIndication(i)
                                }
                                copyIndication={() => {
                                  copyIndication(i);
                                }}
                                disabled={indicationsDisabled}
                                disablePostToTeams={isMobile}
                              />
                            </div>
                          );
                        })}
                      </div>
                      {/* INDICATION DETAILS */}
                    </FormProvider>
                  </div>
                </div>
                <div style={{ flexGrow: 1 }} />

                {/* FOOTER */}
                <div
                  style={{
                    padding: "15px 20px",
                    paddingBottom: 10,
                    backgroundColor: "#f5f5f5",
                    borderBottomRightRadius: 20,
                    borderBottomLeftRadius: 20,
                  }}
                >
                  <Button
                    id="addFOBIndicationSubmitButton"
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    variant="contained"
                    fullWidth
                    // onClick={submitIndications}
                  >
                    SUBMIT
                  </Button>
                  <div
                    style={{
                      paddingTop: 8,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 10,
                      fontWeight: 300,
                      color: "#595959",
                    }}
                  >
                    <Info style={{ fontSize: 18 }} />
                    Starting a new indication will automatically save all active
                    indications.
                  </div>
                </div>
                {/* END FOOTER */}
              </form>
              {/* END BODY */}
            </div>
          </NitroModal>
        </div>
      ) : (
        <NitroModal
          id="AddFOBIndicationPanel"
          open={showAddFOBIndicationPanel}
          className={
            searchStarted
              ? "addFOBIndicationPanel"
              : "addFOBIndicationPanelLeft"
          }
          style={{
            display: "flex",
            flexDirection: "column",
            zIndex: 1004,
          }}
        >
          <div className="FOBIndicationSearchBar" style={{ maxWidth: "100vw" }}>
            <IconButton
              style={{
                borderRadius: 2,
                height: "100%",
                paddingLeft: 12,
                paddingRight: 12,
              }}
              onClick={() => {
                setShowAddFOBIndicationPanel(false);
              }}
            >
              <BackIcon />
            </IconButton>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                height: "100%",
              }}
            >
              <UOMFilter
                value={uom}
                setValue={(value) => setUom(value)}
                isMobile
              />
              <CurrencyFilter
                value={currency}
                setValue={(value) => setCurrency(value)}
                isMobile
              />
            </div>
          </div>
          <div className="FOBIndicationBody">
            {deleteIndicationIndex !== undefined && (
              <DeleteIndicationConfirmation
                confirm={deleteIndication}
                cancel={() => setDeleteIndicationIndex(undefined)}
              />
            )}
            {closeConfirmationOpen && (
              <UnsavedConfirmation
                confirm={() => {
                  form.reset();
                  setShowAddFOBIndicationPanel(false);
                  setCloseConfirmationOpen(false);
                }}
                cancel={() => {
                  setCloseConfirmationOpen(false);
                }}
              />
            )}

            {/* HEADER */}
            <div className="FOBIndicationHeader">
              <div
                style={{
                  paddingLeft: 14,
                  fontWeight: 500,
                  fontSize: 26,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                Market price indications
              </div>
            </div>
            {/* END HEADER */}

            {/* BODY */}
            <form
              onSubmit={form.handleSubmit(submit)}
              style={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
                position: "relative",
              }}
            >
              {form.formState.isSubmitting && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(255,255,255,.7)",
                  }}
                >
                  <ThreeDots color="rgb(25, 25, 25)" />
                </div>
              )}

              <div
                style={{
                  padding: isMobile ? "0px 10px" : "0px 20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    style={{
                      paddingTop: 10,
                      color: "#148DD3",
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: "uppercase",
                    }}
                  >
                    Customer information
                  </div>
                </div>

                <div
                  style={{
                    paddingTop: 10,
                    paddingBottom: 20,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <FormProvider {...form}>
                    {/* FORM */}

                    <div
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        display: "flex",
                        gap: 20,
                      }}
                    >
                      <FormField
                        label="Contact"
                        errorMessage={
                          form.formState.errors.contactName?.message
                        }
                        width="calc((100% - 26px) / 2)"
                      >
                        <TextField
                          {...form.register("contactName")}
                          size="small"
                          fullWidth
                          placeholder="Contact name"
                        />
                      </FormField>
                      <Controller
                        name="party"
                        control={form.control}
                        render={({ field }) => (
                          <FormField
                            label="Company"
                            errorMessage={form.formState.errors.party?.message}
                            width="calc((100% - 20px) / 2)"
                          >
                            <CompanyFilter
                              value={field.value}
                              setValue={field.onChange}
                              isMobile={isMobile}
                            />
                          </FormField>
                        )}
                      />
                    </div>
                    {/* END FORM */}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 20,
                      }}
                    >
                      {/* INDICATION DETAILS */}
                      {indications.fields.map((r, i) => {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={r.product}>
                            <IndicationDetails
                              index={i}
                              total={total}
                              nextIndication={nextIndication}
                              deleteIndication={() => startDeleteIndication(i)}
                              copyIndication={() => {
                                copyIndication(i);
                              }}
                              disabled={indicationsDisabled}
                              disablePostToTeams={isMobile}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {/* INDICATION DETAILS */}
                  </FormProvider>
                </div>
              </div>
              <div style={{ flexGrow: 1 }} />

              {/* FOOTER */}
              <div
                style={{
                  padding: "15px 20px",
                  paddingBottom: 10,
                  backgroundColor: "#f5f5f5",
                  borderBottomRightRadius: 20,
                  borderBottomLeftRadius: 20,
                }}
              >
                <Button
                  id="addFOBIndicationSubmitButton"
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  variant="contained"
                  fullWidth
                  // onClick={submitIndications}
                >
                  SUBMIT
                </Button>
                <div
                  style={{
                    paddingTop: 8,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 10,
                    fontWeight: 300,
                    color: "#595959",
                  }}
                >
                  <Info style={{ fontSize: 18 }} />
                  Starting a new indication will automatically save all active
                  indications.
                </div>
              </div>
              {/* END FOOTER */}
            </form>
            {/* END BODY */}
          </div>
        </NitroModal>
      )}
    </>
  );
};

export default AddFOBIndicationPanel;
